import { verificationResultSchema, type VerificationResult } from '@upl/evidence-schema';

export type OpenAiProviderAvailability = 'AVAILABLE' | 'NOT_CONFIGURED' | 'DISABLED' | 'UNAVAILABLE' | 'UNKNOWN';

export async function getOpenAiProviderAvailability(): Promise<OpenAiProviderAvailability> {
  try {
    const response = await fetch('/v1/capabilities', { headers: { accept: 'application/json' } });
    if (!response.ok) return 'UNKNOWN';
    const value = await response.json() as { adapters?: Array<{ id?: string; availability?: string }> };
    const provider = value.adapters?.find(({ id }) => id === 'openai-provenance');
    if (!provider) return 'UNKNOWN';
    if (provider.availability === 'AVAILABLE' || provider.availability === 'NOT_CONFIGURED' || provider.availability === 'DISABLED' || provider.availability === 'UNAVAILABLE') {
      return provider.availability;
    }
    return 'UNKNOWN';
  } catch {
    return 'UNKNOWN';
  }
}

export async function checkOpenAiProvenance(file: File, idempotencyKey: string): Promise<VerificationResult> {
  const form = new FormData();
  form.append('artifact', file);
  const response = await fetch('/v1/provider-checks/openai', {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'idempotency-key': idempotencyKey,
      'x-origntd-provider-consent': 'openai-media-transmission-v1',
    },
    body: form,
  });
  const body = await response.json() as unknown;
  if (!response.ok) {
    const error = body as { error?: { code?: string; message?: string } };
    throw new ProviderCheckError(error.error?.code ?? 'PROVIDER_CHECK_FAILED', error.error?.message ?? 'The provider check could not complete.');
  }
  const result = verificationResultSchema.parse(body);
  const providerFailure = result.processing.errors.find(({ adapter }) => adapter === 'openai-provenance');
  const providerEvidence = result.signals.some(({ source }) => source.adapter === 'openai-provenance');
  if (providerFailure && !providerEvidence) {
    throw new ProviderCheckError(providerFailure.code, 'The OpenAI provider check was unavailable.');
  }
  return result;
}

export class ProviderCheckError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'ProviderCheckError';
  }
}
